// src/redux/sagas/widgetEnrichmentSaga.ts

import { put, select, takeEvery, delay } from "redux-saga/effects";
import { SiteContent } from "../../types/siteContent";
import { RootState } from "../slices/rootReducer";
import { fetchMenuItemsSuccess } from "../slices/menuItemsSlice";
import { enrichWidgetsDataSuccess } from "../slices/siteContentSlice";
import {
  isPopularItemsWidget,
  isLimitedTimeWidget,
} from "../../utils/widgetPatterns";

import { updateWidgetsWithMenuData } from "../../utils/widgetDataUpdater";
import { updateWidgetsWithLimitedTimeData } from "../../utils/limitedTimeDataUpdater";

function* enrichWidgetsWithMenuData(action: any): Generator<any, void, any> {
  try {
    const menuItems = Array.isArray(action.payload) ? action.payload : [];

    let siteContentData: SiteContent | null = yield select(
      (state: RootState) => state.siteContent.data,
    );

    if (!siteContentData) {
      yield delay(1000);
      siteContentData = yield select(
        (state: RootState) => state.siteContent.data,
      );
    }

    if (!siteContentData) return;

    const hasPopularWidget =
      siteContentData.widgets?.some((widget: any) =>
        isPopularItemsWidget(widget.type),
      ) ||
      siteContentData.homeWidgets?.some((widget: any) =>
        isPopularItemsWidget(widget.type),
      );

    const hasLimitedWidget =
      siteContentData.widgets?.some((widget: any) =>
        isLimitedTimeWidget(widget.type),
      ) ||
      siteContentData.homeWidgets?.some((widget: any) =>
        isLimitedTimeWidget(widget.type),
      );

    if (!hasPopularWidget && !hasLimitedWidget) return;

    let updatedWidgets = siteContentData.widgets || [];
    let updatedHomeWidgets = siteContentData.homeWidgets || [];

    // 1. Popular/Standard widgets get the FULL unfiltered menu items array (Untouched)
    if (hasPopularWidget) {
      updatedWidgets = updateWidgetsWithMenuData(updatedWidgets, menuItems);
      updatedHomeWidgets = updateWidgetsWithMenuData(updatedHomeWidgets, menuItems);
    }

    // 2. Limited-time widgets get ONLY strictly active and non-expired meal-time items
    if (hasLimitedWidget) {
      const limitedTimeItems = menuItems.filter((item: any) => {
        const hasMealTimes = Array.isArray(item.mealTimes) && item.mealTimes.length > 0;
        const isActiveAndNotExpired = item.isMealTimeActive === true && item.isMealTimeExpired !== true;
        
        return hasMealTimes && isActiveAndNotExpired;
      });

      updatedWidgets = updateWidgetsWithLimitedTimeData(updatedWidgets, limitedTimeItems);
      updatedHomeWidgets = updateWidgetsWithLimitedTimeData(updatedHomeWidgets, limitedTimeItems);
    }

    yield put(
      enrichWidgetsDataSuccess({
        widgets: updatedWidgets,
        homeWidgets: updatedHomeWidgets,
      }),
    );
  } catch (error) {
    console.error("[WidgetDataEnrichment] Failed to enrich widget data with menu items", error);
  }
}

export function* watchWidgetDataEnrichment() {
  yield takeEvery(fetchMenuItemsSuccess.type, enrichWidgetsWithMenuData);
}